/**
 * ============================================================================
 * A2A Process Status Panel - Reusable Component
 * ============================================================================
 * 
 * Displays real-time process status with 4 phases and detailed messaging
 * 
 * Features:
 * - Visual phase indicator with progress
 * - Real-time status messages
 * - Start/Stop/Pause controls
 * - Error handling display
 * - Duration tracking
 * 
 * REUSABLE BLUEPRINT for all A2A systems on AppsOkay platform
 */

import React from 'react';
import { Play, Pause, Square, Clock, AlertCircle, CheckCircle2, Info, Activity } from 'lucide-react';
import { 
  ProcessState, 
  ProcessPhase, 
  MessageType,
  formatDuration,
  getPhaseColorClass,
  getMessageColorClass 
} from '../a2a-process-manager';

/**
 * Phase Progress Indicator
 */
export const PhaseIndicator = ({ phases, currentPhase, progress }) => {
  const allPhases = Object.values(ProcessPhase);
  
  return (
    <div className="space-y-4">
      {/* Phase Timeline */}
      <div className="relative">
        <div className="flex items-center justify-between">
          {allPhases.map((phase, index) => {
            const isActive = currentPhase?.id === phase.id;
            const isCompleted = currentPhase && currentPhase.id > phase.id;
            const isFuture = !currentPhase || currentPhase.id < phase.id;
            
            return (
              <div key={phase.id} className="flex-1 relative">
                {/* Connector Line */}
                {index < allPhases.length - 1 && (
                  <div className={`absolute top-5 left-1/2 w-full h-0.5 ${
                    isCompleted ? getPhaseColorClass(phase, 'bg') : 'bg-gray-200'
                  }`} />
                )}
                
                {/* Phase Circle */}
                <div className="relative flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium border-2 z-10 ${
                    isActive 
                      ? `${getPhaseColorClass(phase, 'bg')} text-white border-transparent animate-pulse`
                      : isCompleted
                        ? `${getPhaseColorClass(phase, 'bg')} text-white border-transparent`
                        : 'bg-white text-gray-400 border-gray-300'
                  }`}>
                    {isCompleted ? '✓' : phase.icon}
                  </div>
                  
                  <div className="mt-2 text-center">
                    <div className={`text-sm font-medium ${
                      isActive ? getPhaseColorClass(phase, 'text') : isCompleted ? 'text-gray-700' : 'text-gray-400'
                    }`}>
                      Phase {phase.id}
                    </div>
                    <div className={`text-xs ${
                      isActive ? 'text-gray-700' : 'text-gray-500'
                    }`}>
                      {phase.name}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 font-medium">Overall Progress</span>
          <span className="text-gray-900 font-bold">{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div 
            className={`h-3 rounded-full transition-all duration-500 ${
              currentPhase ? getPhaseColorClass(currentPhase, 'bg') : 'bg-blue-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      
      {/* Current Phase Description */}
      {currentPhase && (
        <div className={`p-3 rounded-lg border-l-4 ${getPhaseColorClass(currentPhase, 'border')} bg-gray-50`}>
          <div className="flex items-center space-x-2">
            <span className="text-lg">{currentPhase.icon}</span>
            <div>
              <div className="font-medium text-gray-900">{currentPhase.name}</div>
              <div className="text-sm text-gray-600">{currentPhase.description}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Status Message Icon
 */
const MessageIcon = ({ type }) => {
  switch (type) {
    case MessageType.SUCCESS:
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    case MessageType.ERROR:
      return <AlertCircle className="w-4 h-4 text-red-600" />;
    case MessageType.WARNING:
      return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    case MessageType.PROCESS:
      return <Activity className="w-4 h-4 text-blue-600 animate-pulse" />;
    default:
      return <Info className="w-4 h-4 text-gray-600" />;
  }
};

/**
 * Status Messages List
 */
export const StatusMessagesList = ({ messages, maxHeight = '400px' }) => {
  const messagesEndRef = React.useRef(null);
  
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  if (messages.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>No activity yet. Start the process to see status messages.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2 overflow-y-auto pr-2" style={{ maxHeight }}>
      {messages.map((msg) => (
        <div 
          key={msg.id} 
          className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="flex-shrink-0 mt-0.5">
            <MessageIcon type={msg.type} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-900">{msg.agent}</span>
              <span className="text-xs text-gray-500">{msg.timestampDisplay}</span>
            </div>
            <div className="flex items-start space-x-2">
              {msg.phase && (
                <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600 flex-shrink-0">
                  {msg.phase}
                </span>
              )}
              <p className={`text-sm ${getMessageColorClass(msg.type)} break-words`}>
                {msg.message}
              </p>
            </div>
          </div>
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

/**
 * Process Control Buttons
 */
export const ProcessControls = ({ status, onStart, onStop, onPause, onResume, onReset }) => {
  const { 
    canStart, 
    canStop, 
    canPause, 
    canResume, 
    isRunning,
    isPaused,
    isCompleted,
    isError,
    duration 
  } = status;
  
  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex items-center space-x-3">
        {canStart && (
          <button
            onClick={onStart}
            className="flex items-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            <Play className="w-5 h-5" />
            <span>Start Process</span>
          </button>
        )}
        
        {canPause && (
          <button
            onClick={onPause}
            className="flex items-center space-x-2 px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
          >
            <Pause className="w-5 h-5" />
            <span>Pause</span>
          </button>
        )}
        
        {canResume && (
          <button
            onClick={onResume}
            className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            <Play className="w-5 h-5" />
            <span>Resume</span>
          </button>
        )}
        
        {canStop && (
          <button
            onClick={onStop}
            className="flex items-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            <Square className="w-5 h-5" />
            <span>Stop</span>
          </button>
        )}
        
        {(isCompleted || isError) && onReset && (
          <button
            onClick={onReset}
            className="flex items-center space-x-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            <Activity className="w-5 h-5" />
            <span>Reset</span>
          </button>
        )}
      </div>
      
      {/* Status Indicator */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${
            isRunning ? 'bg-green-500 animate-pulse' :
            isPaused ? 'bg-yellow-500' :
            isCompleted ? 'bg-blue-500' :
            isError ? 'bg-red-500' :
            'bg-gray-400'
          }`} />
          <span className="text-sm font-medium text-gray-700">
            Status: <span className="text-gray-900">
              {isRunning ? 'Running' :
               isPaused ? 'Paused' :
               isCompleted ? 'Completed' :
               isError ? 'Error' :
               'Ready'}
            </span>
          </span>
        </div>
        
        {duration > 0 && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Clock className="w-4 h-4" />
            <span>{formatDuration(duration)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Complete Process Status Panel
 */
export const ProcessStatusPanel = ({ 
  status, 
  onStart, 
  onStop, 
  onPause, 
  onResume,
  onReset,
  showControls = true,
  showPhases = true,
  showMessages = true,
  maxMessagesHeight = '400px'
}) => {
  const { currentPhase, progress, statusMessages, errors } = status;
  
  return (
    <div className="space-y-6">
      {/* Control Panel */}
      {showControls && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Process Control</h3>
          <ProcessControls 
            status={status}
            onStart={onStart}
            onStop={onStop}
            onPause={onPause}
            onResume={onResume}
            onReset={onReset}
          />
        </div>
      )}
      
      {/* Phase Progress */}
      {showPhases && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Process Phases</h3>
          <PhaseIndicator 
            phases={Object.values(ProcessPhase)}
            currentPhase={currentPhase}
            progress={progress}
          />
        </div>
      )}
      
      {/* Error Summary */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-900 mb-2">
                {errors.length} Error{errors.length !== 1 ? 's' : ''} Encountered
              </h4>
              <div className="space-y-1">
                {errors.map((err) => (
                  <div key={err.id} className="text-sm text-red-800">
                    <span className="font-medium">{err.agent}:</span> {err.error}
                    {err.recoverable && <span className="text-red-600 ml-2">(recoverable)</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Status Messages */}
      {showMessages && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Log</h3>
          <StatusMessagesList 
            messages={statusMessages}
            maxHeight={maxMessagesHeight}
          />
        </div>
      )}
    </div>
  );
};

export default ProcessStatusPanel;

